import { notification } from 'antd';
import { UTCTimestamp } from 'lightweight-charts';
import React, {
  createContext,
  Dispatch,
  SetStateAction,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useDeepCompareEffect } from 'react-use';

import { ApiCharts } from 'api';
import { BaseStrategy } from 'api/cex/base_strategy';
import { handleNewBalanceCharts, handleNewCharts, mergeCandles } from 'context/charts/utils';
import { smoothBalanceSeries, smoothTotalBalanceSeries } from 'entities/cex/lib/balanceSmoothing';
import { IAccountWithBalance, useAccountsWithBalances } from 'entities/cex-account';
import { CexOrder } from 'entities/cex-order';
import { useActiveOrders, useCexSockets } from 'hooks/cex';
import { useLoadCexCharts } from 'hooks/charts';
import { chartNames } from 'shared/consts/charts';
import { useTypedDispatch, useTypedSelector } from 'store';
import { setCexKlines } from 'store/slices/charts';
import { Bus } from 'tools';
import { BalanceChartPoint, ETradingViewEvents, ICexChartPoint, KLine } from 'types/charts';
import { filterOrdersByBotFilters } from 'utils/cexBots';
import { chartTimeframeToInterval, timeframeToRange } from 'utils/charts';

import { ChartsControlsContext } from './ChartsControlsContext';

const INITIAL_LIMIT = 500;
const PAGINATION_LIMIT = 300;

interface IChartsLoadContext {
  loadPaginationCharts: ({
    startTime,
    endTime,
  }: {
    startTime: number;
    endTime: number;
  }) => Promise<ICexChartPoint[] | undefined>;

  orderbookDepth: {
    set: Dispatch<SetStateAction<number>>;
    get: number;
  };

  //records
  candleStickRecords: {
    time: UTCTimestamp;
    close: number;
    high: number;
    low: number;
    open: number;
  }[];
  balancesChartRecords: {
    records: {
      time: UTCTimestamp;
      value: number;
    }[];
    label: string;
  }[];
  totalBalanceUsdRecords: {
    time: UTCTimestamp;
    value: number;
  }[];
  totalVolumeRecords: {
    time: UTCTimestamp;
    value: number;
  }[];
  internalVolumeRecords: {
    time: UTCTimestamp;
    value: number;
  }[];
  loading: boolean;
  setLoading: Dispatch<SetStateAction<boolean>>;
  paginationLoading: boolean;
  setPaginationLoading: Dispatch<SetStateAction<boolean>>;
  hasMorePagination: boolean;
  setHasMorePagination: Dispatch<SetStateAction<boolean>>;
  pricePrecision: number | undefined;
  strategy: BaseStrategy | undefined;
  chartArgs: () => {
    interval: number;
    endTime: number;
    startTime: number;
    limit: number;
  };
  chartPaginationArgs: () => {
    limit: number;
    interval: number;
  };
  activeOrdersCount: number;
  allOrders: CexOrder[]; // All orders without bot filter
  orders: CexOrder[]; // Orders filtered by selectedBots
  ordersLoading: boolean;
  onCancelOrders: (
    orders: { account_id: number; cex_order_id: string }[],
  ) => Promise<{ isSuccess: boolean }>;
  onCancelByAccount: (accountId: number) => Promise<{ isSuccess: boolean }>;
  cancelingOrderLoading: string | undefined;
  setPlacedOrderId: (v: string) => void;
  refetchData?: () => void;

  accounts: IAccountWithBalance[];
  filterOrdersByBots: (orders: CexOrder[], botFilters: string[]) => CexOrder[];
}

export const ChartsLoadContext = createContext<IChartsLoadContext>({
  loadPaginationCharts: async () => undefined,

  orderbookDepth: {
    set: () => {},
    get: 20,
  },

  //records
  candleStickRecords: [],
  balancesChartRecords: [],
  totalBalanceUsdRecords: [],
  totalVolumeRecords: [],
  internalVolumeRecords: [],
  loading: false,
  setLoading: () => {},
  paginationLoading: false,
  setPaginationLoading: () => {},
  hasMorePagination: false,
  setHasMorePagination: () => {},
  pricePrecision: undefined,
  strategy: undefined,
  chartArgs: () => ({
    interval: 0,
    endTime: 0,
    startTime: 0,
    limit: 0,
  }),
  chartPaginationArgs: () => ({
    limit: 0,
    interval: 0,
  }),
  activeOrdersCount: 0,
  allOrders: [],
  orders: [],
  ordersLoading: false,
  onCancelOrders: async () => {
    return { isSuccess: false };
  },
  onCancelByAccount: async () => {
    return { isSuccess: false };
  },
  cancelingOrderLoading: undefined,
  setPlacedOrderId: () => {},
  refetchData: () => {},

  accounts: [],
  filterOrdersByBots: orders => orders,
});

export const ChartsLoadContextProvider: React.FC<{ children?: React.ReactNode }> = ({
  children,
}) => {
  const cexPair = useTypedSelector(store => store.pairs.selectedCexPair)!;
  const dispatch = useTypedDispatch();
  const [notificationApi, contextHolder] = notification.useNotification();

  const { accounts, initialAccountsLoading } = useAccountsWithBalances({
    pairId: cexPair?.id,
    cex: cexPair?.cex,
    symbol: cexPair ? `${cexPair.token_base.symbol}-${cexPair.token_quote.symbol}` : '',
    enabled: Boolean(cexPair),
  });
  const {
    selectedAccounts,
    timeframe,
    priceFormat,
    resetBalancesShow,
    accountsToAPI,
    selectedBots,
    balanceSmoothing,
  } = useContext(ChartsControlsContext);

  const [records, setRecords] = useState<ICexChartPoint[]>([]);
  const [recordsBalance, setRecordsBalance] = useState<BalanceChartPoint[]>([]);
  const [orderbookDepth, setOrderbookDepth] = useState<number>(20);
  const [useBalanceChartFallback, setUseBalanceChartFallback] = useState<boolean>(false);

  // Use ref to avoid stale closure in Bus event handlers
  const recordsRef = useRef<ICexChartPoint[]>([]);

  useEffect(() => {
    recordsRef.current = records;
    dispatch(setCexKlines(records));
  }, [dispatch, records]);

  const [loading, setLoading] = useState<boolean>(true);
  const [paginationLoading, setPaginationLoading] = useState<boolean>(false);
  const [hasMorePagination, setHasMorePagination] = useState<boolean>(true);

  const smoothBalancesIfNeeded = useCallback(
    (series: Parameters<typeof smoothBalanceSeries>[0]) =>
      balanceSmoothing.get ? smoothBalanceSeries(series) : series,
    [balanceSmoothing.get],
  );

  const smoothTotalBalancesIfNeeded = useCallback(
    (series: Parameters<typeof smoothTotalBalanceSeries>[0]) =>
      balanceSmoothing.get ? smoothTotalBalanceSeries(series) : series,
    [balanceSmoothing.get],
  );

  const chartArgs = useCallback(() => {
    const interval = chartTimeframeToInterval(timeframe.get);
    const { endTime, startTime } = timeframeToRange(timeframe.get);

    return {
      startTime: startTime,
      endTime: endTime,
      limit: INITIAL_LIMIT,
      interval,
    };
  }, [timeframe.get]);

  const chartPaginationArgs = useCallback(() => {
    const limit = PAGINATION_LIMIT;
    const interval = chartTimeframeToInterval(timeframe.get);

    return { limit, interval };
  }, [timeframe.get]);

  const {
    getOrders,
    activeOrders,
    activeOrdersCount,
    loading: ordersLoading,
    onCancelByAccount,
    onCancelOrders,
    cancelingOrderLoading,
    setPlacedOrderId,
  } = useActiveOrders({ accounts, pairId: cexPair.id, notificationApi });

  const { precision: pricePrecision, strategy } = useCexSockets({
    interval: useMemo(() => chartTimeframeToInterval(timeframe.get), [timeframe.get]),
    orderbookDepth,
  });

  const loadCexCharts = useLoadCexCharts();

  const recordsToCharts = useCallback((rec: ICexChartPoint[]) => {
    return {
      candleStickRecords: rec.map(el => ({
        time: el.time as UTCTimestamp,
        close: el.close,
        high: el.high,
        low: el.low,
        open: el.open,
      })),
      totalBalanceUsdRecords: rec.map(el => ({
        time: el.time,
        value: el.balanceTotalUsd,
      })) as {
        time: UTCTimestamp;
        value: number;
      }[],
      totalVolumeRecords: rec.map(el => ({
        time: el.time,
        value: el.volume,
      })) as {
        time: UTCTimestamp;
        value: number;
      }[],
      internalVolumeRecords: rec.map(el => ({
        time: el.time,
        value: el.internalVolume,
      })) as {
        time: UTCTimestamp;
        value: number;
      }[],
    };
  }, []);

  const recordsToBalances = useCallback(
    (rec: BalanceChartPoint[]) => ({
      balancesChartRecords: [
        {
          label: cexPair.token_base.symbol,
          records: rec.map(el => ({
            time: el.time as UTCTimestamp,
            value: priceFormat === 'token' ? el.baseBalance : el.baseBalanceUsd,
          })),
        },
        {
          label: cexPair.token_quote.symbol,
          records: rec.map(el => ({
            time: el.time as UTCTimestamp,
            value: priceFormat === 'token' ? el.quoteBalane : el.quoteBalanceUsd,
          })),
        },
      ],
    }),
    [cexPair, priceFormat],
  );

  const loadComboCexCharts = useCallback(
    async ({
      startTime,
      endTime,
      interval,
    }: {
      startTime: number;
      endTime: number;
      interval: number;
    }) => {
      const accountsResult = selectedAccounts.get.length === 0 ? accounts : selectedAccounts.get;

      const { points, usedFallback } = await loadCexCharts({
        startTime,
        endTime,
        timeframe: timeframe.get,
        accounts: accountsResult,
        interval,
      });

      return { points, usedFallback };
    },
    [loadCexCharts, timeframe.get, accounts, selectedAccounts.get],
  );

  const loadBalanceChart = useCallback(
    async ({ startTime, endTime }: { startTime: number; endTime: number }) => {
      const accountsResult = selectedAccounts.get.length === 0 ? accounts : selectedAccounts.get;

      const response = await ApiCharts.getBalanceChart({
        symbol: `${cexPair.token_base.symbol}-${cexPair.token_quote.symbol}`,
        cex: [cexPair.cex],
        period: timeframe.get,
        from: startTime,
        to: endTime,
        account_id: accountsResult.map(account => account.id),
      });

      return {
        pointsBalance: response.data?.points || [],
      };
    },
    [cexPair, timeframe.get, accounts, selectedAccounts.get],
  );

  const loadCharts = useCallback(async () => {
    try {
      resetBalancesShow();

      setLoading(true);
      setHasMorePagination(true);

      const { startTime, endTime, limit, interval } = chartArgs();

      if (selectedAccounts.get.length) {
        const { pointsBalance } = await loadBalanceChart({
          startTime,
          endTime,
        });

        const dataBalance = handleNewBalanceCharts({ points: pointsBalance });

        if (dataBalance) {
          setRecordsBalance(dataBalance);

          if (dataBalance.length < limit) setHasMorePagination(false);
        }
      } else {
        const { points, usedFallback } = await loadComboCexCharts({
          startTime,
          endTime,
          interval,
        });

        const data = handleNewCharts({ points });

        if (data) {
          setRecords(data);

          if (data.length < limit) setHasMorePagination(false);
        }

        if (usedFallback && accounts.length) {
          const { pointsBalance } = await loadBalanceChart({
            startTime,
            endTime,
          });

          const dataBalance = handleNewBalanceCharts({ points: pointsBalance });
          if (dataBalance?.length) {
            setRecordsBalance(dataBalance);
            setUseBalanceChartFallback(true);
          } else {
            setRecordsBalance([]);
            setUseBalanceChartFallback(false);
          }
        } else {
          setRecordsBalance([]);
          setUseBalanceChartFallback(false);
        }
      }
    } catch (error) {
      console.log('error: ', error);
    } finally {
      setLoading(false);
    }
  }, [
    resetBalancesShow,
    chartArgs,
    selectedAccounts.get.length,
    loadBalanceChart,
    loadComboCexCharts,
    accounts.length,
  ]);

  const loadPaginationCharts = useCallback(
    async ({ startTime, endTime }: { startTime: number; endTime: number }) => {
      setPaginationLoading(true);

      try {
        const { limit, interval } = chartPaginationArgs();

        const { points } = await loadComboCexCharts({
          startTime,
          endTime,
          interval,
        });

        const data = handleNewCharts({ points });

        if (data) {
          if (data.length < limit) setHasMorePagination(false);

          setRecords(v => {
            const newData = mergeCandles(v, data);

            const newRecords = recordsToCharts(newData);
            const newBalances = recordsToBalances(newData);
            const processedBalances = smoothBalancesIfNeeded(newBalances.balancesChartRecords);
            const processedTotalBalances = smoothTotalBalancesIfNeeded(
              newRecords.totalBalanceUsdRecords,
            );

            setTimeout(() => {
              Bus.emit(`${chartNames.cex.balances}-update-pagination`, {
                balancesChartRecords: processedBalances,
                totalBalancesUsd: processedTotalBalances,
              });
            }, 0);

            setTimeout(() => {
              Bus.emit(`${chartNames.cex.pair}-update-pagination`, {
                candleStickRecords: newRecords.candleStickRecords,
                totalVolumeRecords: newRecords.totalVolumeRecords,
                internalVolumeRecords: newRecords.internalVolumeRecords,
              });
            }, 0);

            return newData;
          });

          setPaginationLoading(false);

          return data;
        }
      } catch (error) {
        console.log('error: ', error);
      }
    },
    [
      chartPaginationArgs,
      loadComboCexCharts,
      setHasMorePagination,
      setPaginationLoading,
      recordsToCharts,
      recordsToBalances,
      smoothBalancesIfNeeded,
      smoothTotalBalancesIfNeeded,
    ],
  );

  useEffect(() => {
    const handler = (data: unknown) => {
      const kline = data as KLine;

      if (!kline) return;

      const [time, open, high, low, close, volume] = kline;

      // Use ref to get current records to avoid stale closure
      const currentRecords = recordsRef.current;
      const lastCandle = currentRecords[currentRecords.length - 1];
      const candleExist = lastCandle?.time === time;

      const updatedCandle = {
        ...lastCandle,
        open: Number(open),
        high: Number(high),
        low: Number(low),
        close: Number(close),
        volume: Number(volume),
        ...(candleExist ? {} : { time }),
      };

      const updatedBalanceCandle = {
        ...lastCandle,
        ...(candleExist ? {} : { time }),
      };

      // Update records state to ensure Redux cexKlines and TradingView refs are updated
      setRecords(prev => {
        const lastIndex = prev.length - 1;
        const lastRecord = prev[lastIndex];

        if (candleExist) {
          // Update existing candle, preserve fields that don't come from WebSocket
          const updated = [...prev];
          updated[lastIndex] = {
            ...updatedCandle,
            internalVolume: lastRecord?.internalVolume ?? 0,
            baseBalance: lastRecord?.baseBalance ?? 0,
            baseBalanceUsd: lastRecord?.baseBalanceUsd ?? 0,
            quoteBalane: lastRecord?.quoteBalane ?? 0,
            quoteBalanceUsd: lastRecord?.quoteBalanceUsd ?? 0,
            balanceTotalUsd: lastRecord?.balanceTotalUsd ?? 0,
          };
          return updated;
        } else {
          // Add new candle - copy balance fields from previous candle as they don't update via WebSocket
          return [
            ...prev,
            {
              ...updatedCandle,
              // Copy previous candle's fields that aren't updated via WebSocket
              internalVolume: 0, // New candle starts with 0 internal volume
              baseBalance: lastRecord?.baseBalance ?? 0,
              baseBalanceUsd: lastRecord?.baseBalanceUsd ?? 0,
              quoteBalane: lastRecord?.quoteBalane ?? 0,
              quoteBalanceUsd: lastRecord?.quoteBalanceUsd ?? 0,
              balanceTotalUsd: lastRecord?.balanceTotalUsd ?? 0,
            },
          ];
        }
      });

      const newRecords = recordsToCharts([updatedCandle]);
      const newBalances = recordsToBalances([
        selectedAccounts.get.length ? updatedBalanceCandle : updatedCandle,
      ]);

      setTimeout(() => {
        const processedBalances = smoothBalancesIfNeeded(newBalances.balancesChartRecords);
        const processedTotal = smoothTotalBalancesIfNeeded(newRecords.totalBalanceUsdRecords);

        Bus.emit(`${chartNames.cex.balances}-update-kline`, {
          balancesChartRecords: processedBalances.map(el => ({
            ...el,
            record: el.records[0],
          })),
          totalBalanceUsd: processedTotal[0],
        });
      }, 0);

      setTimeout(() => {
        Bus.emit(`${chartNames.cex.pair}-update-kline`, {
          candleStickRecord: newRecords.candleStickRecords[0],
          totalVolumeRecord: newRecords.totalVolumeRecords[0],
          internalVolumeRecord: newRecords.internalVolumeRecords[0],
        });
      }, 0);

      setTimeout(() => {
        Bus.emit(ETradingViewEvents.UpdateRecentBars, updatedCandle);
      }, 0);
    };

    Bus.on(`${cexPair.id}-update-kline`, handler);

    return () => {
      Bus.off(`${cexPair.id}-update-kline`, handler);
    };
  }, [
    cexPair,
    timeframe.get,
    selectedAccounts.get,
    recordsToBalances,
    recordsToCharts,
    smoothBalancesIfNeeded,
    smoothTotalBalancesIfNeeded,
  ]);

  useEffect(() => {
    if (!initialAccountsLoading) {
      loadCharts();
    }
    //eslint-disable-next-line
  }, [cexPair, timeframe.get, selectedAccounts.get, initialAccountsLoading]);

  useDeepCompareEffect(() => {
    getOrders({ accountsToAPI, selectedAccounts: accounts });
  }, [cexPair, accountsToAPI, accounts]);

  const { candleStickRecords, internalVolumeRecords, totalBalanceUsdRecords, totalVolumeRecords } =
    useMemo(() => recordsToCharts(records), [records, recordsToCharts]);

  const { balancesChartRecords } = useMemo(() => {
    const shouldUseBalanceChartRecords = selectedAccounts.get.length || useBalanceChartFallback;
    const balances = shouldUseBalanceChartRecords
      ? recordsToBalances(recordsBalance)
      : recordsToBalances(records);

    return {
      balancesChartRecords: smoothBalancesIfNeeded(balances.balancesChartRecords),
    };
  }, [
    records,
    recordsBalance,
    selectedAccounts.get,
    useBalanceChartFallback,
    recordsToBalances,
    smoothBalancesIfNeeded,
  ]);

  const filterOrdersByBots = useCallback(
    (ordersToFilter: CexOrder[], botFilters: string[]) =>
      filterOrdersByBotFilters(ordersToFilter, botFilters),
    [],
  );

  // Filter orders by selected bots
  const filteredActiveOrders = useMemo(() => {
    if (selectedBots.get.length === 0) {
      return activeOrders;
    }

    return filterOrdersByBots(activeOrders, selectedBots.get);
  }, [activeOrders, selectedBots.get, filterOrdersByBots]);

  return (
    <ChartsLoadContext.Provider
      value={{
        loadPaginationCharts,

        orderbookDepth: {
          set: setOrderbookDepth,
          get: orderbookDepth,
        },

        //records
        candleStickRecords,
        internalVolumeRecords,
        totalBalanceUsdRecords,
        totalVolumeRecords,
        balancesChartRecords,
        loading,
        setLoading,
        paginationLoading,
        setPaginationLoading,
        hasMorePagination,
        setHasMorePagination,
        pricePrecision,
        strategy,
        chartArgs,
        chartPaginationArgs,
        activeOrdersCount,
        allOrders: activeOrders,
        orders: filteredActiveOrders,
        ordersLoading,
        onCancelOrders,
        onCancelByAccount,
        cancelingOrderLoading,
        setPlacedOrderId,
        refetchData: loadCharts,

        accounts,
        filterOrdersByBots,
      }}
    >
      {contextHolder}
      {children}
    </ChartsLoadContext.Provider>
  );
};
